<template>
  <div>
    <Head title="Monitoring Laporan Pertanggung Jawaban" />
    <h1 class="mb-8 text-2xl font-bold">Monitoring Laporan Pertanggung Jawaban</h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="flex items-center justify-between w-full p-4 bg-gray-200">
        <span>Filter</span>
        <button class="text-sm text-blue-600 underline" @click="reset">Reset Filter</button>
      </div>
      <div class="flex flex-wrap w-full p-6">
        <div class="w-full lg:w-3/12">Periode Penerima Beasiswa:</div>
        <v-select
          v-model="form.periode_beasiswa" :options="periodeBeasiswas" class="w-full lg:w-9/12"
          :reduce="item => item.id" label="nama" placeholder="Pilih Periode Penerima Beasiswa"
        />
        <div class="w-full mt-2 lg:w-3/12">Periode Pelaporan:</div>
        <v-select
          v-model="form.periode" :options="periodes" class="w-full mt-2 lg:w-9/12" :reduce="periode => periode.id"
          label="nama" placeholder="Pilih Periode Pelaporan"
        />
        <div class="w-full mt-2 lg:w-3/12">Status Laporan:</div>
        <v-select
          v-model="form.status" :options="listStatus" class="w-full mt-2 lg:w-9/12" :reduce="status => status.id"
          label="nama" placeholder="Pilih Status"
        />
        <div class="w-full mt-2 lg:w-3/12">Jenis Perguruan Tinggi:</div>
        <v-select
          v-model="form.status_university" :options="universityTypes" class="w-full mt-2 lg:w-9/12"
          :reduce="type => type.id" label="nama" placeholder="Pilih Jenis Perguruan Tinggi"
        />
        <div class="w-full mt-2 lg:w-3/12">Universitas:</div>
        <v-select
          v-model="form.university" :options="universities" class="w-full mt-2 lg:w-9/12"
          :reduce="university => university.id" label="nama" placeholder="Pilih Universitas"
        />
        <div class="w-full mt-2 lg:w-3/12">Prodi:</div>
        <v-select
          v-model="form.prodi" :options="prodis" class="w-full mt-2 lg:w-9/12" :reduce="prodi => prodi.id"
          label="nama" placeholder="Pilih Prodi"
        />
        <div class="w-full mt-2 lg:w-3/12">Nama / NIM Mahasiswa:</div>
        <text-input v-model="form.search" class="w-full mt-2 lg:w-9/12" placeholder="Cari nama atau NIM..." />
      </div>
    </div>

    <div class="overflow-x-auto bg-white rounded-md shadow">
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">No</th>
            <th class="px-6 pt-6 pb-4">Data Beasiswa</th>
            <th class="px-6 pt-6 pb-4">Data Mahasiswa</th>
            <th class="px-6 pt-6 pb-4">Laporan</th>
            <th class="px-6 pt-6 pb-4">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(application, index) in applications.data" :key="application.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="p-4 border-t align-top">
              {{ (applications.current_page - 1) * applications.per_page + index + 1 }}
            </td>
            <td class="p-4 border-t align-top">
              <div class="text-sm">
                {{ application.periode }}
              </div>
              <ul class="mt-2 text-sm">
                <li class="leading-6">Periode Beasiswa: {{ application.periode_beasiswa }}</li>
                <li class="leading-6">Tgl. Pelaporan: {{ application.tgl_pelaporan }}</li>
                <li class="leading-6">Jenis: {{ application.jenis_pt }} - {{ application.jenis }}</li>
                <li class="leading-6">
                  Nominal Anggaran: Rp. {{ formatCurrency(application.nominal_beasiswa, 0) }}
                </li>
                <li v-if="application.nominal_disetujui" class="leading-6">
                  Nominal Disetujui: Rp. {{ formatCurrency(application.nominal_disetujui, 0) }}
                </li>
              </ul>
            </td>
            <td class="p-4 border-t align-top">
              <ul class="text-sm">
                <li class="leading-6">Nama: {{ application.nama }}</li>
                <li class="leading-6">NIM: {{ application.nim }}</li>
                <li class="leading-6">Universitas: {{ application.nama_universitas }}</li>
                <li class="leading-6">Prodi: {{ application.nama_prodi }}</li>
              </ul>
            </td>
            <td class="p-4 border-t align-top">
              <ul class="text-sm">
                <li class="leading-6">Biaya Pendidikan: Rp. {{ formatCurrency(application.tuition_fee) }}</li>
                <li class="leading-6">Biaya Lainnya: Rp. {{ formatCurrency(application.other_fee) }}</li>
                <li class="leading-6">IPK: {{ application.ipk }}</li>
                <li class="leading-6">Semester: {{ application.semester }}</li>
                <li class="leading-6">No. Rekening: {{ application.account_number }}</li>
              </ul>
            </td>
            <td class="p-4 text-center border-t align-top">
              <span
                :class="statusClass(application.status)"
                class="px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap"
              >
                {{ application.status_label }}
              </span>
              <p v-if="application.catatan_verifikasi" class="mt-2 text-xs text-gray-500">
                {{ application.catatan_verifikasi }}
              </p>
            </td>
          </tr>
          <tr v-if="applications.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="5">
              Tidak ada data laporan pertanggung jawaban.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="applications.links" />
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '@/Shared/TextInput.vue'

export default {
  components: {
    Head,
    Pagination,
    TextInput,
  },
  layout: Layout,
  props: {
    applications: Object,
    filters: Object,
    universities: Array,
    prodis: Array,
    periodes: Array,
    periodeBeasiswas: Array,
  },

  data() {
    return {
      form: {
        university: parseInt(this.filters.university) || null,
        prodi: parseInt(this.filters.prodi) || null,
        periode: parseInt(this.filters.periode) || null,
        periode_beasiswa: parseInt(this.filters.periode_beasiswa) || null,
        search: this.filters.search,
        status: this.filters.status ?? null,
        status_university: this.filters.status_university,
      },
      universityTypes: [
        { id: 'PTN', nama: 'PTN' },
        { id: 'PTNL', nama: 'PTNL' },
        { id: 'PTKL', nama: 'PTKL' },
        { id: 'PTS', nama: 'PTS' },
      ],
      listStatus: [
        { id: 'belum', nama: 'Belum Pelaporan' },
        { id: 0, nama: 'Draft' },
        { id: 1, nama: 'Pemberkasan' },
        { id: 4, nama: 'RAB Diverifikasi' },
        { id: 5, nama: 'Pengajuan PJ' },
        { id: 6, nama: 'Revisi PJ' },
        { id: 7, nama: 'Disetujui PJ' },
      ],
    }
  },

  watch: {
    form: {
      handler: throttle(function () {
        let query = pickBy(this.form)
        this.$inertia.get(
          this.route('monitoring-accountability.index'),
          Object.keys(query).length ? query : { remember: 'forget' },
          {
            preserveState: true,
            preserveScroll: true,
          },
        )
      }, 150),
      deep: true,
    },
  },

  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    formatCurrency(value) {
      if (!value) return '0'
      return new Intl.NumberFormat('id-ID').format(value)
    },
    statusClass(status) {
      return {
        'bg-slate-200 text-slate-700': status === null,
        'bg-gray-200 text-gray-700': status === 0,
        'bg-blue-100 text-blue-700': status === 1,
        'bg-red-100 text-red-700': status === 2 || status === 3,
        'bg-green-100 text-green-700': status === 4,
        'bg-yellow-100 text-yellow-700': status === 5,
        'bg-orange-100 text-orange-700': status === 6,
        'bg-green-200 text-green-800': status === 7,
      }
    },
  },
}
</script>
